import dayjs from 'dayjs';

import { VisibleDateRange } from '../../types';
import { PendingScrollCommand, PlannerSliceCreator, ScrollSlice } from '../types';

/**
 * Builds the next pendingScrollCommand. Monotonic id so the scroll effect fires
 * even when the same date is requested twice in a row.
 */
export const buildScrollCommand = (
  previous: PendingScrollCommand | null,
  target: dayjs.Dayjs,
  align: PendingScrollCommand['align']
): PendingScrollCommand => ({
  target,
  align,
  id: (previous?.id ?? 0) + 1,
});

/**
 * Scroll orchestration state. The store only DESCRIBES scroll intents
 * (pendingScrollTarget / pendingScrollRestoreLeft / pendingScrollCommand);
 * the DOM scrolling itself happens in use-planner-horizontal-lifecycle and the
 * scroll-command effect (store/effects) — never inside store actions.
 */
export const createScrollSlice: PlannerSliceCreator<ScrollSlice> = set => ({
  pendingScrollTarget: null,
  setPendingScrollTarget: (date: dayjs.Dayjs | null) => set({ pendingScrollTarget: date }),

  pendingScrollRestoreLeft: null,
  setPendingScrollRestoreLeft: (scrollLeft: number | null) =>
    set({ pendingScrollRestoreLeft: scrollLeft }),

  pendingScrollCommand: null,

  firstVisibleDate: dayjs(),
  setFirstVisibleDate: (date: dayjs.Dayjs | null) => set({ firstVisibleDate: date }),

  visibleDateRange: { startIndex: 0, endIndex: 0 },
  setVisibleDateRange: (range: VisibleDateRange) =>
    set(state => {
      if (
        state.visibleDateRange.startIndex === range.startIndex &&
        state.visibleDateRange.endIndex === range.endIndex
      ) {
        return state;
      }
      return { visibleDateRange: range };
    }),

  isScrolling: false,
  setIsScrolling: (isScrolling: boolean) => set({ isScrolling }),

  isLoadingDateRange: false,
  setIsLoadingDateRange: (isLoading: boolean) => set({ isLoadingDateRange: isLoading }),

  uiTransitionCount: 0,
  beginUiTransition: () =>
    set(state => ({
      uiTransitionCount: state.uiTransitionCount + 1,
    })),
  endUiTransition: () =>
    set(state => ({
      uiTransitionCount: Math.max(0, state.uiTransitionCount - 1),
    })),
});
